import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Database,
  Zap,
  TrendingUp,
  Clock,
  Activity,
  AlertCircle,
  Settings,
  Link
} from 'lucide-react';
import AIService, { GTMIntegrationHealth, AgentInsight } from '../../services/aiService';

export const GTMSystemsOverview: React.FC = () => {
  const [systems, setSystems] = useState<GTMIntegrationHealth[]>([]);
  const [insights, setInsights] = useState<AgentInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadData = async () => {
    setLoading(true);
    try {
      const [systemsData, insightsData] = await Promise.all([
        AIService.getGTMSystemsHealth(),
        AIService.generateAgentInsights({})
      ]);
      setSystems(systemsData);
      setInsights(insightsData);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load GTM systems data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-netlify-primary" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-netlify-coral-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-netlify-accent" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'border-l-netlify-primary bg-netlify-teal-50';
      case 'warning':
        return 'border-l-netlify-coral-400 bg-netlify-coral-50';
      case 'error':
        return 'border-l-netlify-accent bg-netlify-coral-100';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const healthySystems = systems.filter(s => s.status === 'healthy').length;
  const totalRecords = systems.reduce((sum, s) => sum + s.recordsProcessed, 0);
  const totalErrors = systems.reduce((sum, s) => sum + s.errorCount, 0);

  if (loading && systems.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Key Metrics */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">GTM Systems Integration</h1>
            <p className="text-sm text-gray-500 mt-1">
              Real-time monitoring of data flows across your revenue technology stack
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-xs text-gray-500">
              Last updated: {formatRelativeTime(lastRefresh)}
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-netlify-teal-50 to-netlify-teal-100 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-netlify-primary" />
              <div className="ml-3">
                <p className="text-sm font-medium text-netlify-secondary">Healthy Systems</p>
                <p className="text-2xl font-bold text-netlify-secondary">{healthySystems}/{systems.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-netlify-teal-50 to-netlify-teal-100 rounded-lg p-4">
            <div className="flex items-center">
              <Database className="h-8 w-8 text-netlify-teal-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-netlify-secondary">Records Synced</p>
                <p className="text-2xl font-bold text-netlify-secondary">{totalRecords.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-netlify-coral-50 to-netlify-coral-100 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-8 w-8 text-netlify-coral-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-netlify-coral-700">Total Errors</p>
                <p className="text-2xl font-bold text-netlify-coral-800">{totalErrors}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-netlify-coral-50 to-netlify-teal-50 rounded-lg p-4">
            <div className="flex items-center">
              <Zap className="h-8 w-8 text-netlify-accent" />
              <div className="ml-3">
                <p className="text-sm font-medium text-netlify-secondary">AI Insights</p>
                <p className="text-2xl font-bold text-netlify-secondary">{insights.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Systems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {systems.map((system) => (
            <div
              key={system.systemName}
              className={`border-l-4 bg-white rounded-lg shadow-sm p-4 ${getStatusColor(system.status)}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(system.status)}
                  <h3 className="font-semibold text-gray-900">{system.systemName}</h3>
                </div>
                <Settings className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Sync:</span>
                  <span className="text-gray-900">{formatRelativeTime(system.lastSync)}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Records:</span>
                  <span className="text-gray-900">{system.recordsProcessed.toLocaleString()}</span>
                </div>

                {system.errorCount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Errors:</span>
                    <span className="text-netlify-accent font-medium">{system.errorCount}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Integration Points:</span>
                  <Link className="w-3 h-3 text-blue-500" />
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {system.integrationPoints.slice(0, 3).map((point, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-netlify-teal-100 text-netlify-teal-800"
                    >
                      {point}
                    </span>
                  ))}
                  {system.integrationPoints.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{system.integrationPoints.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI-Powered Insights */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">🤖 Agent-Driven Insights</h2>
          <div className="flex items-center space-x-2">
            <Activity className="w-4 h-4 text-netlify-primary" />
            <span className="text-sm text-netlify-teal-700">Real-time Analysis</span>
          </div>
        </div>

        <div className="space-y-4">
          {insights.map((insight, idx) => (
            <div key={idx} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    insight.confidence > 0.8 ? 'bg-netlify-primary' :
                    insight.confidence > 0.6 ? 'bg-netlify-coral-500' : 'bg-netlify-accent'
                  }`}></div>
                  <span className="text-sm font-medium text-gray-700 capitalize">
                    {insight.type.replace('_', ' ')}
                  </span>
                  <span className="text-xs text-gray-500">
                    {Math.round(insight.confidence * 100)}% confidence
                  </span>
                </div>
                <TrendingUp className="w-4 h-4 text-netlify-primary" />
              </div>

              <p className="text-gray-900 mb-3">{insight.insight}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Suggested Actions:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {insight.suggestedActions.map((action, actionIdx) => (
                      <li key={actionIdx} className="flex items-center space-x-2">
                        <div className="w-1 h-1 bg-netlify-primary rounded-full"></div>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Key Data Points:</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    {Object.entries(insight.dataPoints).map(([key, value], dataIdx) => (
                      <div key={dataIdx} className="flex justify-between">
                        <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}:</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GTMSystemsOverview;